import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Wallet, Coins, Smartphone, Send, LogOut,
  Eye, ArrowUpRight, Search, CheckCircle, Shield, CreditCard, MapPin, UserCircle, Pencil, History,
  Zap, Copy, SignalHigh, Loader2
} from 'lucide-react';
import RandSign from '@/components/RandSign';
import { QRWalletCode } from '@/components/QRWallet';
import { QRCodeSVG } from 'qrcode.react';
import MerchantFinder from '@/components/MerchantFinder';
import KycTierBadge from '@/components/KycTierBadge';
import { clearApiCache } from '@/lib/sw-register';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDialog, setActiveDialog] = useState(null);
  const [postbankData, setPostbankData] = useState(null);

  // Phase 0 — session token in HttpOnly cookie (see src/lib/auth-client.js).
  // Gate access on user profile presence in localStorage instead of token.
  const token = localStorage.getItem('mojo_token');
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');
  const isLoggedIn = !!user?.id;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    fetchWalletData();
  }, []);

  const fetchPostbankBalance = async (idNumber, phoneNumber) => {
    if (!idNumber || !phoneNumber) return;
    try {
      const response = await axios.post(
        `${API}/postbank/customer-search`,
        { national_id: idNumber, phone_number: phoneNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const c = response.data.customer;
      const accounts = Array.isArray(c) ? c : (c?.accounts || []);
      if (response.data.success && accounts.length > 0) {
        // balance can be nested {available:{amount:"5850.00"}} or flat number
        const getBalance = (acc) => {
          if (acc.balance?.available?.amount) return parseFloat(acc.balance.available.amount);
          if (typeof acc.balance === 'number') return acc.balance;
          return 0;
        };
        const totalBalance = accounts.reduce((sum, acc) => sum + getBalance(acc), 0);
        setPostbankData({ customer: c, accounts, totalBalance, getBalance });
      }
    } catch {
      // Postbank balance is optional — fail silently
    }
  };

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data);
      // Keep localStorage user profile in sync with DB
      if (response.data?.user_profile) {
        const stored = JSON.parse(localStorage.getItem('mojo_user') || '{}');
        const merged = { ...stored, ...response.data.user_profile };
        localStorage.setItem('mojo_user', JSON.stringify(merged));
        fetchPostbankBalance(
          response.data.user_profile.id_number,
          response.data.user_profile.phone_number
        );
      }
    } catch (error) {
      toast.error('Failed to fetch wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    // Phase 0 — also clear the HttpOnly cookie server-side via /auth/logout.
    try { await axios.post(`${API}/auth/logout`); } catch (_) { /* offline ok */ }
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#FAF5F0] to-[#F5E8D8]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F2B10C] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#1B0C34]">Loading...</p>
        </div>
      </div>
    );
  }

  const quickActions = [
    { 
      id: 'topup',
      icon: <CreditCard className="w-8 h-8" />, 
      title: 'Add Funds', 
      description: 'Top-up wallet from card',
      color: 'from-[#145539] to-[#1e7049]'
    },
    { 
      id: 'balance',
      icon: <Search className="w-8 h-8" />, 
      title: 'Account Enquiry', 
      description: 'Search Postbank account',
      color: 'from-[#1B0C34] to-[#2D1854]'
    },
    { 
      id: 'token',
      icon: <RandSign className="w-8 h-8" />, 
      title: 'Payment Token', 
      description: 'Generate payment token',
      color: 'from-[#F2B10C] to-[#d99a0a]'
    },
    { 
      id: 'send',
      icon: <Send className="w-8 h-8" />, 
      title: 'Send Money', 
      description: 'Transfer to phone number',
      color: 'from-[#145539] to-[#1e7049]'
    },
    { 
      id: 'ace',
      icon: <Coins className="w-8 h-8" />, 
      title: 'Buy ACE Token', 
      description: 'Purchase stable coin',
      color: 'from-purple-600 to-purple-800'
    },
    { 
      id: 'airtime',
      icon: <Smartphone className="w-8 h-8" />, 
      title: 'Airtime & Data', 
      description: 'Buy airtime or data',
      color: 'from-blue-600 to-blue-800'
    },
    {
      id: 'merchants',
      icon: <MapPin className="w-8 h-8" />,
      title: 'Find Merchants',
      description: 'Locate nearby Mojo Money',
      color: 'from-orange-600 to-orange-800'
    },
    {
      id: 'profile',
      icon: <UserCircle className="w-8 h-8" />,
      title: 'My Profile',
      description: 'View & edit your details',
      color: 'from-[#1B0C34] to-[#3a1e6e]'
    },
    {
      id: 'passkeys',
      icon: <Shield className="w-8 h-8" />,
      title: 'Passkeys & Security',
      description: 'Manage sign-in & devices',
      color: 'from-[#145539] to-[#0f3d29]'
    },
    {
      id: 'transactions',
      icon: <History className="w-8 h-8" />,
      title: 'Transactions',
      description: 'View recent activity',
      color: 'from-teal-600 to-teal-800'
    },
    {
      id: 'nfc',
      icon: <Zap className="w-8 h-8" />,
      title: 'NFC Transfer',
      description: 'Free instant wallet transfers',
      color: 'from-[#F2B10C] to-[#d99a0a]'
    },
    {
      id: 'limes-sim',
      icon: <SignalHigh className="w-8 h-8" />,
      title: 'Get Mojo Air SIM',
      description: 'Order a SIM on the Mojo Air network',
      color: 'from-emerald-600 to-emerald-800'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]" data-testid="customer-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2B10C]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/sticker.svg" alt="Mojo Money" className="h-10 w-10" />
            <div>
              <h1 className="text-xl font-bold text-[#1B0C34]">Mojo Money</h1>
              <p className="text-xs text-[#145539]">Customer App</p>
            </div>
          </div>
          <Button
            data-testid="customer-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            size="sm"
          >
            <LogOut size={16} />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Verification Status */}
        {wallet?.verification_status && (
          <Card className={`mb-4 border-2 ${wallet.verification_status.verified ? 'border-[#145539] bg-[#145539]/5' : 'border-orange-500 bg-orange-50'}`} data-testid="customer-verification-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                {wallet.verification_status.verified ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-[#145539]" />
                    <div className="flex-1">
                      <p className="font-semibold text-[#1B0C34] text-sm">ID Verified</p>
                    </div>
                    <span className="px-2 py-1 bg-[#145539] text-white text-xs font-semibold rounded-full">
                      ✓ VERIFIED
                    </span>
                  </>
                ) : (
                  <>
                    <Shield className="w-6 h-6 text-orange-500" />
                    <div className="flex-1">
                      <p className="font-semibold text-orange-700 text-sm">ID Verification Required</p>
                      <p className="text-xs text-orange-600">Complete verification to unlock all transactions</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        navigate(`/verify?id_number=${user.id_number || ''}&user_type=customer`);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="customer-verify-btn"
                    >
                      <Shield className="mr-1 w-3 h-3" />
                      Verify Now
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Wallet Balance Card */}
        <Card className="mb-6 bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white" data-testid="customer-wallet-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardDescription className="text-white/70">Welcome back,</CardDescription>
                <CardTitle className="text-2xl">{user.full_name}</CardTitle>
              </div>
              {wallet?.wallet_id && (
                <QRWalletCode 
                  walletId={wallet.wallet_id} 
                  userName={user.full_name}
                  userType="customer"
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-4 ${postbankData ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <div>
                <p className="text-sm text-white/70 mb-1">Wallet Balance</p>
                <p className="text-2xl font-bold" data-testid="customer-wallet-balance">R {wallet?.balance?.toFixed(2) || '0.00'}</p>
              </div>
              {postbankData && (
                <div>
                  <p className="text-sm text-white/70 mb-1">Postbank Balance</p>
                  <p className="text-2xl font-bold" data-testid="customer-postbank-balance">R {postbankData.totalBalance.toFixed(2)}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-white/70 mb-1">ACE Tokens</p>
                <p className="text-2xl font-bold" data-testid="customer-ace-balance">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-white/10 rounded-lg">
              <p className="text-xs text-white/70">Wallet ID</p>
              <p className="text-sm font-mono" data-testid="customer-wallet-id">{wallet?.wallet_id}</p>
            </div>
            <KycTierBadge className="mt-3" />
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          {quickActions.map((action, index) => (
            <Card
              key={index}
              className={`card-hover cursor-pointer bg-gradient-to-br ${action.color} text-white`}
              onClick={() => action.id === 'passkeys' ? navigate('/customer/passkeys') : setActiveDialog(action.id)}
              data-testid={`action-${action.id}`}
            >
              <CardHeader className="pb-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                  {action.icon}
                </div>
                <CardTitle className="text-lg">{action.title}</CardTitle>
                <CardDescription className="text-white/70 text-xs">{action.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </main>

      {/* Dialogs */}
      <WalletTopUpDialog open={activeDialog === 'topup'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} />
      <BalanceEnquiryDialog open={activeDialog === 'balance'} onClose={() => setActiveDialog(null)} />
      <PaymentTokenDialog open={activeDialog === 'token'} onClose={() => setActiveDialog(null)} wallet={wallet} />
      <SendMoneyDialog open={activeDialog === 'send'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} />
      <BuyACEDialog open={activeDialog === 'ace'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} wallet={wallet} />
      <AirtimeDataDialog open={activeDialog === 'airtime'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} />
      <MerchantFinder open={activeDialog === 'merchants'} onClose={() => setActiveDialog(null)} />
      <MyProfileDialog
        open={activeDialog === 'profile'}
        onClose={() => setActiveDialog(null)}
        onSaved={fetchWalletData}
      />
      <TransactionsDialog open={activeDialog === 'transactions'} onClose={() => setActiveDialog(null)} />
      <NFCTransferDialog
        open={activeDialog === 'nfc'}
        onClose={() => { setActiveDialog(null); fetchWalletData(); }}
        wallet={wallet}
      />
      <LimesSubscriptionDialog
        open={activeDialog === 'limes-sim'}
        onClose={() => setActiveDialog(null)}
      />
    </div>
  );
};

// Wallet Top-Up Dialog
const WalletTopUpDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    amount: '',
    card_type: 'debit',
    card_number: '',
    card_holder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const calculateFee = () => {
    if (!formData.amount) return { fee: 0, net: 0 };
    const amount = parseFloat(formData.amount);
    const feeRate = formData.card_type === 'debit' ? 0.02 : 0.035;
    const fee = amount * feeRate;
    const net = amount - fee;
    return { fee, net };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const last4 = formData.card_number.replace(/\s/g, '').slice(-4);
      
      const response = await axios.post(
        `${API}/wallet/topup`,
        {
          amount: parseFloat(formData.amount),
          card_type: formData.card_type,
          card_number_last4: last4,
          card_holder_name: formData.card_holder_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Success! R${response.data.net_credited} added to wallet`);
      setFormData({
        amount: '',
        card_type: 'debit',
        card_number: '',
        card_holder_name: '',
        expiry_month: '',
        expiry_year: '',
        cvv: ''
      });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top-up failed');
    } finally {
      setLoading(false);
    }
  };

  const { fee, net } = calculateFee();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="topup-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Add Funds to Wallet</DialogTitle>
          <DialogDescription>Top-up your wallet using debit or credit card</DialogDescription>
        </DialogHeader>

        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-2">
          <p className="text-xs text-[#1B0C34]/70">
            • Debit: 2% fee • Credit: 3.5% fee<br/>
            • Fee deducted from top-up amount
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="customer_topup_amount">Amount (ZAR)</Label>
            <Input
              id="customer_topup_amount"
              type="number"
              step="0.01"
              min="10"
              data-testid="customer-topup-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-xs space-y-1">
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span className="font-semibold">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Fee ({formData.card_type === 'debit' ? '2%' : '3.5%'}):</span>
                  <span className="text-red-600">-R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-[#145539] pt-1 border-t">
                  <span>You receive:</span>
                  <span>R{net.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Card Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="customer-topup-debit-btn"
                variant={formData.card_type === 'debit' ? 'default' : 'outline'}
                className={formData.card_type === 'debit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1 text-sm' : 'flex-1 text-sm'}
                onClick={() => setFormData({ ...formData, card_type: 'debit' })}
              >
                Debit
              </Button>
              <Button
                type="button"
                data-testid="customer-topup-credit-btn"
                variant={formData.card_type === 'credit' ? 'default' : 'outline'}
                className={formData.card_type === 'credit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1 text-sm' : 'flex-1 text-sm'}
                onClick={() => setFormData({ ...formData, card_type: 'credit' })}
              >
                Credit
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="customer_card_holder">Card Holder</Label>
            <Input
              id="customer_card_holder"
              data-testid="customer-card-holder-input"
              value={formData.card_holder_name}
              onChange={(e) => setFormData({ ...formData, card_holder_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="customer_card_number">Card Number</Label>
            <Input
              id="customer_card_number"
              data-testid="customer-card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                setFormData({ ...formData, card_number: formatted });
              }}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label htmlFor="customer_exp_month" className="text-xs">Month</Label>
              <Input
                id="customer_exp_month"
                data-testid="customer-exp-month-input"
                placeholder="MM"
                maxLength="2"
                value={formData.expiry_month}
                onChange={(e) => setFormData({ ...formData, expiry_month: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="customer_exp_year" className="text-xs">Year</Label>
              <Input
                id="customer_exp_year"
                data-testid="customer-exp-year-input"
                placeholder="YY"
                maxLength="2"
                value={formData.expiry_year}
                onChange={(e) => setFormData({ ...formData, expiry_year: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="customer_cvv" className="text-xs">CVV</Label>
              <Input
                id="customer_cvv"
                type="password"
                data-testid="customer-cvv-input"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="customer-submit-topup-btn"
            className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Add Funds'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Balance Enquiry Dialog (Postbank REST API - JSON-RPC 2.0)
const BalanceEnquiryDialog = ({ open, onClose }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleEnquiry = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/customer-search`,
        { 
          phone_number: mobileNumber,
          national_id: idNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.customer) {
        setCustomer(response.data.customer);
        toast.success('Customer information retrieved');
      } else {
        toast.error(response.data.error || 'Customer not found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retrieve customer information');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMobileNumber('');
    setIdNumber('');
    setCustomer(null);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="balance-enquiry-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Postbank Account Enquiry</DialogTitle>
          <DialogDescription>Find nearby Mojo Money/ Spazacash merchants</DialogDescription>
        </DialogHeader>

        {!customer ? (
          <form onSubmit={handleEnquiry} className="space-y-4">
            <div>
              <Label htmlFor="mobile_num">Mobile Number</Label>
              <Input
                id="mobile_num"
                data-testid="balance-mobile-input"
                placeholder="+27769584862"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">Format: +27XXXXXXXXX</p>
            </div>
            <div>
              <Label htmlFor="id_num">National ID Number</Label>
              <Input
                id="id_num"
                data-testid="balance-id-input"
                placeholder="7503085486088"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ''))}
                maxLength={13}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">13-digit SA ID</p>
            </div>
            <Button
              type="submit"
              data-testid="check-balance-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Account'}
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg" data-testid="balance-result">
              <p className="text-sm opacity-70 mb-2">Postbank Account</p>
              {(customer?.firstName || customer?.lastName) && (
                <p className="text-xl font-bold mb-1">{customer.firstName} {customer.lastName}</p>
              )}
              {customer?.phoneNumber && (
                <p className="text-xs opacity-70 mb-2">{customer.phoneNumber}</p>
              )}

              {(() => {
                const accs = Array.isArray(customer) ? customer : (customer?.accounts || []);
                const getBal = (acc) => {
                  if (acc.balance?.available?.amount) return parseFloat(acc.balance.available.amount);
                  if (typeof acc.balance === 'number') return acc.balance;
                  return null;
                };
                const total = accs.reduce((s, a) => s + (getBal(a) || 0), 0);
                return accs.length > 0 && (
                  <div className="space-y-1 text-sm border-t border-white/20 pt-3 mt-2">
                    {total > 0 && (
                      <div className="flex justify-between mb-2">
                        <span className="text-sm opacity-70">Total Balance:</span>
                        <span className="text-lg font-bold">R {total.toFixed(2)}</span>
                      </div>
                    )}
                    <p className="text-xs opacity-70 mb-2">Linked Accounts:</p>
                    {accs.map((account, idx) => {
                      const bal = getBal(account);
                      return (
                        <div key={idx} className="py-1">
                          <div className="flex justify-between">
                            <span className="text-xs">{account.accountName || account.alias || account.type}</span>
                            <span className="text-xs font-mono">{account.accountNumber || account.number}</span>
                          </div>
                          {account.linkedAccount && (
                            <div className="flex justify-between mt-0.5">
                              <span className="text-xs opacity-60">Linked:</span>
                              <span className="text-xs font-mono">{account.linkedAccount}</span>
                            </div>
                          )}
                          {bal != null && (
                            <div className="flex justify-between mt-0.5">
                              <span className="text-xs opacity-60">Available:</span>
                              <span className="text-xs font-semibold">R {bal.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <Button
              onClick={handleReset}
              data-testid="new-search-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            >
              New Search
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Payment Token Dialog
const PaymentTokenDialog = ({ open, onClose, wallet }) => {
  const [mode, setMode] = useState('generate'); // 'generate' | 'redeem'
  const [formData, setFormData] = useState({ amount: '', token_source: 'wallet' });
  const [redeemInput, setRedeemInput] = useState('');
  const [token, setToken] = useState(null);
  const [redeemResult, setRedeemResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const authToken = localStorage.getItem('mojo_token');

  useEffect(() => {
    if (open) fetchACEInfo();
  }, [open]);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateACE = () => {
    if (!formData.amount || !aceInfo) return 0;
    return (parseFloat(formData.amount) / aceInfo.current_value_zar).toFixed(4);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/wallet/generate-payment-token`,
        { amount: parseFloat(formData.amount), token_source: formData.token_source },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setToken(response.data);
      toast.success('Payment token generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    const code = redeemInput.trim().toUpperCase();
    if (!code) { toast.error('Please enter a payment token'); return; }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/wallet/redeem-token`,
        { payment_token: code },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setRedeemResult(response.data);
      toast.success(`Payment of R${response.data.amount?.toFixed(2)} processed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token redemption failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setToken(null);
    setRedeemResult(null);
    setFormData({ amount: '', token_source: 'wallet' });
    setRedeemInput('');
    setMode('generate');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="payment-token-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Payment Token</DialogTitle>
          <DialogDescription>Generate a token for a merchant to scan, or enter a merchant's token to pay</DialogDescription>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === 'generate' ? 'default' : 'outline'}
            className={mode === 'generate' ? 'flex-1 bg-[#F2B10C] text-[#1B0C34]' : 'flex-1'}
            onClick={() => { setMode('generate'); setToken(null); setRedeemResult(null); }}
          >
            Generate Token
          </Button>
          <Button
            type="button"
            variant={mode === 'redeem' ? 'default' : 'outline'}
            className={mode === 'redeem' ? 'flex-1 bg-[#1B0C34] text-white' : 'flex-1'}
            onClick={() => { setMode('redeem'); setToken(null); setRedeemResult(null); }}
          >
            Redeem Token
          </Button>
        </div>

        {/* Balance Display */}
        <div className="grid grid-cols-2 gap-3 p-3 bg-[#FAF5F0] rounded-lg">
          <div>
            <p className="text-xs text-[#1B0C34]/60">Wallet Balance</p>
            <p className="text-sm font-bold text-[#1B0C34]">R{wallet?.balance?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-xs text-[#1B0C34]/60">ACE Balance</p>
            <p className="text-sm font-bold text-[#F2B10C]">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
          </div>
        </div>

        {mode === 'generate' ? (
          <>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <Label>Token Source</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    data-testid="select-wallet-token-btn"
                    variant={formData.token_source === 'wallet' ? 'default' : 'outline'}
                    className={formData.token_source === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                    onClick={() => setFormData({ ...formData, token_source: 'wallet' })}
                  >
                    Wallet (ZAR)
                  </Button>
                  <Button
                    type="button"
                    data-testid="select-ace-token-btn"
                    variant={formData.token_source === 'ace_token' ? 'default' : 'outline'}
                    className={formData.token_source === 'ace_token' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                    onClick={() => setFormData({ ...formData, token_source: 'ace_token' })}
                  >
                    ACE Token
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="token_amount">Amount (ZAR)</Label>
                <Input
                  id="token_amount"
                  type="number"
                  step="0.01"
                  data-testid="token-amount-input"
                  placeholder="Enter amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
                {formData.amount && formData.token_source === 'ace_token' && aceInfo && (
                  <p className="text-xs text-[#145539] font-medium mt-1">
                    Requires: {calculateACE()} ACE tokens (@ R{aceInfo.current_value_zar}/token)
                  </p>
                )}
              </div>
              <Button
                type="submit"
                data-testid="generate-token-btn"
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate Payment Token'}
              </Button>
            </form>

            {token && (
              <div className="mt-2 p-4 bg-[#145539] text-white rounded-lg" data-testid="payment-token-result">
                <p className="text-sm opacity-70 mb-1">Payment Token</p>
                <p className="text-2xl font-bold font-mono mb-3">{token.payment_token}</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="opacity-70">Amount:</span>
                    <span className="font-semibold">R{token.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-70">Source:</span>
                    <span className="font-semibold capitalize">{token.token_source}</span>
                  </div>
                  {token.ace_amount && (
                    <div className="flex justify-between">
                      <span className="opacity-70">ACE Tokens:</span>
                      <span className="font-semibold">{token.ace_amount.toFixed(4)} ACE</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="opacity-70">Expires in:</span>
                    <span className="font-semibold">{Math.floor(token.expires_in / 60)} mins</span>
                  </div>
                </div>
                <p className="text-xs mt-3 opacity-80 bg-white/10 p-2 rounded">
                  Show this token to the merchant — valid for 15 minutes
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <form onSubmit={handleRedeem} className="space-y-4">
              <div>
                <Label htmlFor="redeem_token_input">Enter Merchant Token</Label>
                <Input
                  id="redeem_token_input"
                  data-testid="redeem-token-input"
                  placeholder="MP..."
                  value={redeemInput}
                  onChange={(e) => setRedeemInput(e.target.value.toUpperCase())}
                  className="font-mono text-lg tracking-widest"
                  maxLength={12}
                />
                <p className="text-xs text-[#1B0C34]/50 mt-1">
                  Enter the token code shown by the merchant (starts with MP)
                </p>
              </div>
              <Button
                type="submit"
                data-testid="redeem-token-btn"
                className="w-full bg-[#1B0C34] hover:bg-[#2D1854] text-white"
                disabled={loading || !redeemInput.trim()}
              >
                {loading ? 'Processing...' : 'Pay with Token'}
              </Button>
            </form>

            {redeemResult && (
              <div className="mt-2 p-4 bg-[#145539] text-white rounded-lg" data-testid="redeem-token-result">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <p className="font-semibold">Payment Successful</p>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="opacity-70">Amount paid:</span>
                    <span className="font-bold">R{redeemResult.amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Send Money Dialog
const SendMoneyDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({ recipient_phone: '', amount: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/money-transfer`,
        { 
          recipient_phone: formData.recipient_phone, 
          amount: parseFloat(formData.amount),
          transfer_type: 'send'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success('Money sent successfully');
      setFormData({ recipient_phone: '', amount: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send money');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="send-money-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Send Money</DialogTitle>
          <DialogDescription>Transfer money using ACE stable coin</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <Label htmlFor="send_phone">Recipient Phone Number</Label>
            <Input
              id="send_phone"
              data-testid="send-phone-input"
              placeholder="+27XXXXXXXXX"
              value={formData.recipient_phone}
              onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="send_amount">Amount (ZAR)</Label>
            <Input
              id="send_amount"
              type="number"
              step="0.01"
              data-testid="send-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="submit-send-money-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Money'}
          </Button>
        </form>

        {result && result.transfer_code && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="send-money-result">
            <p className="text-sm opacity-70">Transfer Code</p>
            <p className="text-2xl font-bold">{result.transfer_code}</p>
            <p className="text-xs mt-2">Share this code with the recipient</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Buy ACE Dialog
const BuyACEDialog = ({ open, onClose, wallet }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    if (open) {
      fetchACEInfo();
    }
  }, [open]);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateTokens = () => {
    if (!amount || !aceInfo) return '0.0000';
    return (parseFloat(amount) / aceInfo.current_value_zar).toFixed(4);
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ace-token`,
        { amount: parseFloat(amount), operation: 'buy' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Purchased ${response.data.ace_tokens} ACE tokens @ R${response.data.token_value}`);
      setAmount('');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to buy ACE tokens');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="buy-ace-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Buy ACE Token</DialogTitle>
          <DialogDescription>Carbon tax-backed token on Hedera DLT</DialogDescription>
        </DialogHeader>
        
        {aceInfo && (
          <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg mb-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-80">Current Token Value</p>
                <p className="text-2xl font-bold">R {aceInfo.current_value_zar}</p>
              </div>
              <Coins className="w-12 h-12 opacity-20" />
            </div>
            <div className="mt-3 pt-3 border-t border-white/20">
              <p className="text-xs opacity-80">💹 Based on SA Carbon Tax (R{aceInfo.carbon_tax_rate}/tonne - 25%)</p>
              <p className="text-xs opacity-80 mt-1">🔄 {aceInfo.days_remaining_in_cycle} days left in {aceInfo.minting_cycle_years}yr cycle</p>
            </div>
          </div>
        )}

        <div className="p-4 bg-[#FAF5F0] rounded-lg">
          <p className="text-sm text-[#1B0C34]/60">Your Wallet Balance</p>
          <p className="text-2xl font-bold text-[#1B0C34]">R {wallet?.balance?.toFixed(2) || '0.00'}</p>
        </div>
        
        <form onSubmit={handleBuy} className="space-y-4">
          <div>
            <Label htmlFor="ace_buy_amount">Amount to Spend (ZAR)</Label>
            <Input
              id="ace_buy_amount"
              type="number"
              step="0.01"
              data-testid="ace-buy-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            {aceInfo && amount && (
              <p className="text-sm text-[#145539] font-medium mt-2">
                ≈ {calculateTokens()} ACE tokens
              </p>
            )}
            {aceInfo && (
              <p className="text-xs text-[#1B0C34]/60 mt-1">
                1 ACE = R{aceInfo.current_value_zar} • Value appreciates with carbon tax
              </p>
            )}
          </div>
          <Button
            type="submit"
            data-testid="submit-buy-ace-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing on Hedera DLT...' : 'Buy ACE Tokens'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Airtime & Data Dialog — backed by the real Mojo Air (Limes) catalogue.
// The old /airtime-data path fabricated a voucher and provisioned nothing
// (audit A7); this places a real order and charges the wallet.
const AirtimeDataDialog = ({ open, onClose }) => {
  const token = localStorage.getItem('mojo_token');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [form, setForm] = useState({ msisdn: '', sku: '' });
  const [loading, setLoading] = useState(false);

  const normalizeMsisdn = (raw) => {
    const d = String(raw || '').replace(/\D/g, '');
    if (d.startsWith('27')) return d;
    if (d.startsWith('0')) return '27' + d.slice(1);
    if (d.length === 9) return '27' + d;
    return d;
  };

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoadingProducts(true);
    axios
      .get(`${API}/limes/products?page=1&limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!alive) return;
        const raw = r.data?.data || r.data?.items || [];
        const items = (Array.isArray(raw) ? raw : [])
          .filter((p) => p && p.sku && /^\d+$/.test(String(p.sku)) && p.name && Number(p.price) > 0)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setProducts(items);
      })
      .catch((e) => {
        const d = e?.response?.data?.detail || e?.message;
        toast.error(`Failed to load catalogue: ${typeof d === 'string' ? d : 'Unknown error'}`);
      })
      .finally(() => alive && setLoadingProducts(false));
    return () => { alive = false; };
  }, [open, token]);

  const chosen = products.find((p) => String(p.sku) === String(form.sku));

  const handlePurchase = async (e) => {
    e.preventDefault();
    const msisdn = normalizeMsisdn(form.msisdn);
    if (!/^27\d{9}$/.test(msisdn)) return toast.error('Enter a valid SA number, e.g. 0821234567');
    if (!form.sku) return toast.error('Please pick a product');
    setLoading(true);
    try {
      await axios.post(
        `${API}/limes/order`,
        { msisdn, products: [{ id: form.sku }] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${chosen?.name || 'Product'} sent to ${msisdn}`);
      setForm({ msisdn: '', sku: '' });
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Purchase failed';
      const s = typeof detail === 'string' ? detail : JSON.stringify(detail);
      if (s.toLowerCase().includes('subscriber does not exist')) {
        toast.error('That number is not an active Mojo Air subscriber.');
      } else {
        toast.error(s);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="airtime-data-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Buy Airtime & Data</DialogTitle>
          <DialogDescription>Pick a bundle from the Mojo Air catalogue. Charged to your wallet.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handlePurchase} className="space-y-4">
          <div>
            <Label htmlFor="customer-airtime-product">Product</Label>
            <Select value={form.sku} onValueChange={(v) => setForm({ ...form, sku: v })} disabled={loadingProducts}>
              <SelectTrigger data-testid="customer-airtime-product-select">
                <SelectValue placeholder={loadingProducts ? 'Loading catalogue…' : 'Pick a product'} />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.sku} value={String(p.sku)}>
                    {p.name} — R{Number(p.price).toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="customer_phone">Recipient number</Label>
            <Input
              id="customer_phone"
              data-testid="customer-airtime-phone-input"
              placeholder="0821234567"
              value={form.msisdn}
              onChange={(e) => setForm({ ...form, msisdn: e.target.value })}
              required
            />
          </div>
          {chosen && (
            <p className="text-sm text-gray-600" data-testid="customer-airtime-cost-preview">
              Cost: <span className="font-semibold text-[#1B0C34]">R{Number(chosen.price).toFixed(2)}</span> — charged to your wallet.
            </p>
          )}
          <Button
            type="submit"
            data-testid="customer-submit-airtime-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading || loadingProducts || !form.sku}
          >
            {loading ? 'Processing…' : chosen ? `Buy for R${Number(chosen.price).toFixed(2)}` : 'Buy'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Transactions Dialog
const TransactionsDialog = ({ open, onClose }) => {
  const token = localStorage.getItem('mojo_token');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) fetchTransactions();
  }, [open]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wallet/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34] flex items-center gap-2">
            <History className="w-5 h-5" /> Recent Transactions
          </DialogTitle>
          <DialogDescription>Your last 50 wallet transactions</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-center text-[#1B0C34]/60 py-10">No transactions yet</p>
        ) : (
          <div className="overflow-y-auto flex-1 space-y-2 pr-1">
            {transactions.map((txn, i) => {
              const positive = txn.amount >= 0;
              return (
                <div key={i} className="flex justify-between items-start p-3 bg-[#FAF5F0] rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1B0C34] truncate">{txn.description || '—'}</p>
                    <p className="text-xs text-[#1B0C34]/50 mt-0.5">
                      {txn.transaction_type?.replace(/_/g, ' ').toUpperCase()}
                    </p>
                    <p className="text-xs text-[#1B0C34]/40">{formatDate(txn.created_at)}</p>
                  </div>
                  <div className="text-right ml-3 shrink-0">
                    <p className={`text-sm font-bold ${positive ? 'text-[#145539]' : 'text-red-600'}`}>
                      {positive ? '+' : ''}R {Math.abs(txn.amount).toFixed(2)}
                    </p>
                    {txn.fee > 0 && (
                      <p className="text-xs text-[#1B0C34]/50">Fee: R {txn.fee.toFixed(2)}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SA_PROVINCES_CUSTOMER = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
];

// My Profile Dialog
const MyProfileDialog = ({ open, onClose, onSaved }) => {
  const token = localStorage.getItem('mojo_token');

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    province: '',
    address: '',
  });

  useEffect(() => {
    if (open) {
      const u = JSON.parse(localStorage.getItem('mojo_user') || '{}');
      setUserData(u);
      setEditing(false);
      setForm({
        full_name: u.full_name || '',
        email: u.email || '',
        phone_number: u.phone_number || '',
        province: u.province || '',
        address: u.business_address || u.address || '',
      });
    }
  }, [open]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/customer/profile`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedUser = { ...userData, ...form, business_address: form.address };
      localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
      // Drop the SW's /wallet/balance cache so a refresh / re-open
      // doesn't render the pre-save user_profile.
      await clearApiCache();
      toast.success('Profile updated successfully');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34] flex items-center gap-2">
            <UserCircle className="w-5 h-5" /> Profile &amp; Settings
          </DialogTitle>
          <DialogDescription>Manage your account details and contact information</DialogDescription>
        </DialogHeader>

        {!editing ? (
          <div className="space-y-6">
            {/* Edit Profile button top-right */}
            <div className="flex justify-end">
              <Button
                onClick={() => setEditing(true)}
                className="bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold px-6"
              >
                Edit Profile
              </Button>
            </div>

            {/* Personal Information */}
            <div>
              <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Full Name</Label>
                  <div className="border border-gray-200 rounded-md px-3 py-2 text-sm text-[#1B0C34] bg-white min-h-[38px]">
                    {form.full_name || '—'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">ID Number</Label>
                  <div className="border border-gray-200 rounded-md px-3 py-2 text-sm text-[#1B0C34]/50 bg-gray-50 min-h-[38px]">
                    {userData.id_number || ''}
                  </div>
                  <p className="text-xs text-[#1B0C34]/50 mt-1">ID number cannot be changed</p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Email Address</Label>
                  <div className="border border-gray-200 rounded-md px-3 py-2 text-sm text-[#1B0C34] bg-white min-h-[38px]">
                    {form.email || '—'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Phone Number</Label>
                  <div className="border border-gray-200 rounded-md px-3 py-2 text-sm text-[#1B0C34] bg-white min-h-[38px]">
                    {form.phone_number || '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Status */}
            <div>
              <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Verification Status</h3>
              {userData.biometric_verified ? (
                <div className="flex items-center gap-2 text-[#145539]">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">ID Verified</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[#F2B10C]">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-medium">ID Not Verified</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Full Name</Label>
                  <Input
                    value={form.full_name}
                    onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">ID Number</Label>
                  <div className="border border-gray-200 rounded-md px-3 py-2 text-sm text-[#1B0C34]/50 bg-gray-50 min-h-[38px]">
                    {userData.id_number || ''}
                  </div>
                  <p className="text-xs text-[#1B0C34]/50 mt-1">ID number cannot be changed</p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-base font-semibold text-[#1B0C34] mb-3">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Email Address</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Phone Number</Label>
                  <Input
                    value={form.phone_number}
                    onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))}
                    placeholder="+27..."
                  />
                </div>
              </div>
            </div>

            {/* Province & Address */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Province</Label>
                <select
                  value={form.province}
                  onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-[#1B0C34]"
                >
                  <option value="">Select province</option>
                  {SA_PROVINCES_CUSTOMER.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-[#1B0C34]/60 mb-1 block">Address</Label>
                <Input
                  value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Street address"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// NFC Transfer Dialog
const NFCTransferDialog = ({ open, onClose, wallet }) => {
  const [mode, setMode] = useState('send'); // 'send' | 'receive'
  const [amount, setAmount] = useState('');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeemInput, setRedeemInput] = useState('');
  const [redeemResult, setRedeemResult] = useState(null);
  const authToken = localStorage.getItem('mojo_token');

  const handleClose = () => {
    setAmount('');
    setToken(null);
    setMode('send');
    setRedeemInput('');
    setRedeemResult(null);
    onClose();
  };

  const handleGenerateToken = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/wallet/generate-payment-token`,
        { amount: parseFloat(amount), token_source: 'wallet' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setToken(response.data);
      toast.success('Transfer token generated — FREE transfer!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    const code = redeemInput.trim().toUpperCase();
    if (!code) { toast.error('Please enter or paste a token'); return; }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/wallet/redeem-token`,
        { payment_token: code },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setRedeemResult(response.data);
      toast.success(`R${response.data.amount?.toFixed(2)} received successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token redemption failed');
    } finally {
      setLoading(false);
    }
  };

  // QR value encodes the token for the receiver to scan
  const qrValue = token ? JSON.stringify({
    type: 'mojo_wallet_transfer',
    token: token.payment_token,
    amount: token.amount,
    app: 'MojoMoney'
  }) : '';

  const expiresMinutes = token ? Math.floor((token.expires_in || 300) / 60) : 5;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34] flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#F2B10C]" /> NFC Wallet Transfer
          </DialogTitle>
          <DialogDescription>
            Send or receive money instantly via NFC or QR code — FREE transfers!
          </DialogDescription>
        </DialogHeader>

        {/* Green info banner */}
        <div className="flex items-center gap-2 p-3 bg-[#145539] text-white rounded-lg">
          <Zap className="w-4 h-4 shrink-0" />
          <div>
            <p className="text-xs font-semibold">⚡ NFC Wallet Transfer</p>
            <p className="text-xs opacity-80">FREE instant wallet-to-wallet transfers via NFC or QR code</p>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === 'send' ? 'default' : 'outline'}
            className={mode === 'send' ? 'flex-1 bg-[#F2B10C] text-[#1B0C34] font-semibold' : 'flex-1'}
            onClick={() => { setMode('send'); setToken(null); setAmount(''); setRedeemResult(null); }}
          >
            <Send className="w-4 h-4 mr-1" /> Send Money
          </Button>
          <Button
            type="button"
            variant={mode === 'receive' ? 'default' : 'outline'}
            className={mode === 'receive' ? 'flex-1 bg-[#145539] text-white font-semibold' : 'flex-1'}
            onClick={() => { setMode('receive'); setToken(null); setAmount(''); setRedeemResult(null); }}
          >
            <ArrowUpRight className="w-4 h-4 mr-1 rotate-180" /> Receive Money
          </Button>
        </div>

        {mode === 'send' ? (
          <>
            {!token ? (
              <form onSubmit={handleGenerateToken} className="space-y-4">
                <div>
                  <Label htmlFor="nfc_amount">Amount to Send (R)</Label>
                  <Input
                    id="nfc_amount"
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                    className="text-lg font-semibold"
                  />
                </div>
                <div className="p-3 bg-[#FAF5F0] rounded-lg">
                  <p className="text-xs text-[#1B0C34]/60">Available Balance</p>
                  <p className="text-sm font-bold text-[#1B0C34]">R {wallet?.balance?.toFixed(2) || '0.00'}</p>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !amount}
                  className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                  size="lg"
                >
                  {loading ? 'Generating...' : <><Zap className="mr-2 w-4 h-4" /> Generate Transfer Token</>}
                </Button>
                <p className="text-xs text-center text-[#1B0C34]/50">
                  Generate a secure token for the receiver to scan
                </p>
              </form>
            ) : (
              <div className="space-y-4">
                {/* Success summary */}
                <div className="p-4 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-[#145539]" />
                    <p className="font-semibold text-[#145539]">Transfer Token Generated</p>
                  </div>
                  <div className="space-y-1 text-sm text-[#1B0C34]">
                    <p><span className="font-semibold">Amount:</span> R{token.amount?.toFixed(2)}</p>
                    <p><span className="font-semibold">Token ID:</span> <span className="font-mono">{token.payment_token}</span></p>
                    <p className="flex items-center gap-1">
                      <span className="font-semibold">Expires in:</span> {expiresMinutes} minutes
                    </p>
                  </div>
                </div>

                {/* QR Code */}
                <div className="border-2 border-dashed border-[#F2B10C] rounded-lg p-4 flex flex-col items-center gap-3">
                  <QRCodeSVG
                    value={qrValue}
                    size={200}
                    level="M"
                    includeMargin={true}
                  />
                  <p className="text-xs text-center text-[#1B0C34]/60">
                    Receiver scans this QR code to receive R{token.amount?.toFixed(2)}
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setToken(null); setAmount(''); }}
                  className="w-full"
                >
                  Generate Another Token
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            {!redeemResult ? (
              <form onSubmit={handleRedeem} className="space-y-4">
                <div>
                  <Label htmlFor="nfc_redeem_input">Transfer Token Data</Label>
                  <textarea
                    id="nfc_redeem_input"
                    rows={4}
                    placeholder={`Paste the token data here or scan QR code (format: {"type":"mojo_wallet_transfer",...})`}
                    value={redeemInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      // If it looks like JSON, try to extract the token field
                      if (val.trim().startsWith('{')) {
                        try {
                          const parsed = JSON.parse(val.trim());
                          if (parsed.token) {
                            setRedeemInput(parsed.token);
                            return;
                          }
                        } catch { /* not valid JSON yet, keep as-is */ }
                      }
                      setRedeemInput(val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12) || val);
                    }}
                    className="w-full border border-[#F2B10C] rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-[#F2B10C] resize-y min-h-[80px]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !redeemInput.trim()}
                  className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                  size="lg"
                >
                  {loading ? 'Processing...' : <><ArrowUpRight className="mr-2 w-4 h-4" /> Receive Money</>}
                </Button>
                <p className="text-xs text-center text-[#1B0C34]/50">
                  Scan the sender's QR code or paste the token data above
                </p>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-[#145539]/10 border border-[#145539]/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-[#145539]" />
                    <p className="font-semibold text-[#145539]">Money Received!</p>
                  </div>
                  <p className="text-2xl font-bold text-[#1B0C34]">R {redeemResult.amount?.toFixed(2)}</p>
                  <p className="text-xs text-[#1B0C34]/60 mt-1">Added to your wallet — FREE transfer</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setRedeemResult(null); setRedeemInput(''); }}
                  className="w-full"
                >
                  Receive Another
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Mojo Air SIM subscription dialog (powered by Limes API) — multi-step flow:
//  1) load packages from /api/limes/subscription-products
//  2) user picks a package
//  3) user fills delivery address
//  4) POST /api/limes/subscriptions → show provisioned MSISDN
const LimesSubscriptionDialog = ({ open, onClose }) => {
  const token = localStorage.getItem('mojo_token');
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');

  const [step, setStep] = useState('packages');     // packages | address | success | error
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [address, setAddress] = useState({
    streetNo: '',
    streetName: '',
    suburb: '',
    city: '',
    stateOrProvince: 'Gauteng',
    postCode: '',
    country: 'South Africa',
  });

  useEffect(() => {
    if (!open) return;
    setStep('packages');
    setSelectedPkg(null);
    setResult(null);
    setErrorMsg('');
    loadPackages();
  }, [open]);

  const loadPackages = async () => {
    setLoadingPackages(true);
    try {
      const response = await axios.get(
        `${API}/limes/subscription-products?category=gsm_packages_soa&page=1&limit=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPackages(response.data?.data || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to load packages';
      setErrorMsg(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setStep('error');
    } finally {
      setLoadingPackages(false);
    }
  };

  const handlePickPackage = (pkg) => {
    setSelectedPkg(pkg);
    setStep('address');
  };

  const handleSubmit = async () => {
    const required = ['streetNo', 'streetName', 'suburb', 'city', 'stateOrProvince', 'postCode'];
    for (const key of required) {
      if (!address[key]?.trim()) {
        toast.error(`Please fill in ${key}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/limes/subscriptions`,
        { productId: selectedPkg.id, eSim: false, address },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      setStep('success');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Subscription failed';
      setErrorMsg(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setStep('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="limes-sim-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SignalHigh className="text-emerald-600" /> Get a Mojo Air SIM
          </DialogTitle>
          <DialogDescription>
            Order a SIM on the Mojo Air network. Your new mobile number will be provisioned automatically.
          </DialogDescription>
        </DialogHeader>

        {step === 'packages' && (
          <div className="space-y-3">
            {loadingPackages ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin text-emerald-600" size={28} />
              </div>
            ) : packages.length === 0 ? (
              <p className="text-sm text-gray-600 py-4 text-center">
                No SIM packages available right now. Please try again later.
              </p>
            ) : (
              packages.map((pkg) => (
                <Card
                  key={pkg.id}
                  className="cursor-pointer hover:border-emerald-500 transition"
                  onClick={() => handlePickPackage(pkg)}
                  data-testid={`limes-pkg-${pkg.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-[#1B0C34]">{pkg.name}</p>
                        <p className="text-xs text-gray-500">{(pkg.brand || '').replace(/Limes\s*Mobile/i, 'Mojo Air') || 'Mojo Air'} · {pkg.sku}</p>
                        {pkg.description && pkg.description !== pkg.name && (
                          <p className="text-xs text-gray-600 mt-1">{pkg.description}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-emerald-700">
                          {pkg.price > 0 ? `R ${parseFloat(pkg.price).toFixed(2)}` : 'Free'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {step === 'address' && selectedPkg && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="text-sm font-semibold text-emerald-900">{selectedPkg.name}</p>
              <p className="text-xs text-emerald-700">{(selectedPkg.brand || '').replace(/Limes\s*Mobile/i, 'Mojo Air') || 'Mojo Air'} · {selectedPkg.id}</p>
            </div>
            <p className="text-sm text-gray-700">Where should we deliver your SIM?</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Street No</Label>
                <Input
                  value={address.streetNo}
                  onChange={(e) => setAddress({ ...address, streetNo: e.target.value })}
                  data-testid="limes-streetno"
                />
              </div>
              <div>
                <Label className="text-xs">Street Name</Label>
                <Input
                  value={address.streetName}
                  onChange={(e) => setAddress({ ...address, streetName: e.target.value })}
                  data-testid="limes-streetname"
                />
              </div>
              <div>
                <Label className="text-xs">Suburb</Label>
                <Input
                  value={address.suburb}
                  onChange={(e) => setAddress({ ...address, suburb: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input
                  value={address.city}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Province</Label>
                <Input
                  value={address.stateOrProvince}
                  onChange={(e) => setAddress({ ...address, stateOrProvince: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Postal Code</Label>
                <Input
                  value={address.postCode}
                  onChange={(e) => setAddress({ ...address, postCode: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep('packages')}>
                Back
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="limes-submit-btn"
              >
                {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Provisioning...</> : 'Order SIM'}
              </Button>
            </div>
          </div>
        )}

        {step === 'success' && result?.data && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-center">
              <CheckCircle className="text-emerald-600 mx-auto mb-2" size={40} />
              <p className="font-semibold text-emerald-900">SIM Ordered!</p>
              <p className="text-xs text-emerald-700 mt-1">Your Mojo Air number is ready.</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b pb-1">
                <span className="text-gray-600">MSISDN:</span>
                <span className="font-mono font-semibold" data-testid="limes-result-msisdn">
                  {result.data.msisdn || '—'}
                </span>
              </div>
              {result.data.iccid && (
                <div className="flex justify-between border-b pb-1">
                  <span className="text-gray-600">ICCID:</span>
                  <span className="font-mono text-xs">{result.data.iccid}</span>
                </div>
              )}
              <div className="flex justify-between border-b pb-1">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold">{result.data.status || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Subscription ID:</span>
                <span className="font-mono text-xs">{result.data.subscriptionId || '—'}</span>
              </div>
            </div>
            <p className="text-xs text-gray-600 italic">
              Your physical SIM will be delivered to the address provided. You can top up airtime &amp; data on this number once it's active on the network.
            </p>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onClose}>
              Done
            </Button>
          </div>
        )}

        {step === 'error' && (
          <div className="space-y-3">
            <div className="bg-red-50 border border-red-200 rounded p-4">
              <p className="font-semibold text-red-900 text-sm">Something went wrong</p>
              <p className="text-xs text-red-700 mt-1 break-all">{errorMsg}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
              <Button className="flex-1" onClick={() => { setErrorMsg(''); loadPackages(); setStep('packages'); }}>
                Try Again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerDashboard;
